import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Button, Badge, Spinner, Text,
  makeStyles, tokens,
} from '@fluentui/react-components'
import { ArrowLeft24Regular, Edit24Regular } from '@fluentui/react-icons'
import {
  pensionContributionsService,
  FrequencyLabels,
  ContributionTypeLabels,
} from '../../services/pensionContributionsService'
import { useAuth } from '../../auth/useAuth'

const useStyles = makeStyles({
  header:     { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' },
  card:       { backgroundColor: tokens.colorNeutralBackground1, borderRadius: tokens.borderRadiusMedium, padding: '24px', boxShadow: tokens.shadow4, maxWidth: '800px' },
  infoGrid:   { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 32px', marginTop: '16px' },
  infoLabel:  { fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.07em', color: tokens.colorNeutralForeground3, marginBottom: '2px' },
  infoValue:  { fontSize: '14px', fontWeight: 600, color: tokens.colorNeutralForeground1, lineHeight: '1.4' },
  sectionTitle: { fontSize: tokens.fontSizeBase400, fontWeight: tokens.fontWeightSemibold, paddingBottom: '10px', borderBottom: `1px solid ${tokens.colorNeutralStroke2}` },
  subsection: { fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.06em', color: tokens.colorNeutralForeground2, marginTop: '20px', paddingTop: '16px', borderTop: `1px solid ${tokens.colorNeutralBackground3}` },
})

function InfoField({ label, value }: { label: string; value?: string | null }) {
  const styles = useStyles()
  if (!value) return null
  return (
    <div>
      <div className={styles.infoLabel}>{label}</div>
      <div className={styles.infoValue}>{value}</div>
    </div>
  )
}

const fmt = (d: string | null | undefined) =>
  d ? d.slice(0, 10).split('-').reverse().join('.') : null

function isEnded(validTo: string | null | undefined): boolean {
  if (!validTo) return false
  return validTo < new Date().toISOString().slice(0, 10)
}

export default function PensionContributionDetailPage() {
  const styles = useStyles()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { isHR, isAdmin } = useAuth()
  const canEdit = isHR || isAdmin

  const { data, isLoading } = useQuery({
    queryKey: ['pension-contribution', id],
    queryFn: () => pensionContributionsService.getById(Number(id)),
    enabled: !!id,
  })

  if (isLoading) return <Spinner label="Načítám..." />
  if (!data) return <Text>Záznam nenalezen.</Text>

  const ended = isEnded(data.validTo)

  return (
    <div>
      <div className={styles.header}>
        <Button appearance="subtle" icon={<ArrowLeft24Regular />} onClick={() => navigate('/pension-contributions')}>
          Zpět
        </Button>
        <Text size={600} weight="semibold">{data.employeeFullName}</Text>
        <Badge appearance="filled" color={ended ? 'subtle' : 'success'}>
          {ended ? 'Ukončen' : 'Aktivní'}
        </Badge>
        {canEdit && (
          <Button appearance="primary" icon={<Edit24Regular />} onClick={() => navigate(`/pension-contributions/${id}/edit`)}>
            Upravit
          </Button>
        )}
      </div>

      <div className={styles.card}>
        <Text className={styles.sectionTitle}>Příspěvek na stáří</Text>
        <div className={styles.infoGrid} style={{ marginTop: 16 }}>
          <InfoField label="Zaměstnanec"     value={data.employeeFullName} />
          <InfoField label="Oddělení"        value={data.departmentName} />
          <InfoField label="Penzijní ústav / správce DIP" value={data.pensionProvider} />
          <InfoField label="Číslo účtu"      value={data.accountNumber} />
          <InfoField label="Variabilní symbol" value={data.variableSymbol} />
          <InfoField label="Specifický symbol" value={data.specificSymbol} />
          <InfoField label="Výše příspěvku"  value={`${data.amount.toLocaleString('cs-CZ')} Kč`} />
          <InfoField label="Frekvence"       value={FrequencyLabels[data.frequency]} />
          <InfoField label="Typ příspěvku"   value={ContributionTypeLabels[data.contributionType]} />
          <InfoField label="Zahájení výplaty" value={fmt(data.validFrom)} />
          <InfoField label="Ukončení výplaty" value={fmt(data.validTo)} />
        </div>

        {data.note && (
          <>
            <div className={styles.subsection}>Poznámka</div>
            <div style={{ fontSize: 14, color: tokens.colorNeutralForeground2, fontStyle: 'italic', marginTop: 8, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
              {data.note}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
